<template>
  <div>
    <!-- Filter Card -->
    <b-card :title="$t('audit.searchTitle')">
      <b-form @submit.prevent="onSearch">
        <b-row>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              :label="$t('audit.startDate')"
              label-for="auditFrom"
            >
              <flat-pickr
                v-model="filters.from"
                class="form-control"
                :placeholder="$t('audit.startDate')"
                :config="dateConfig"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              :label="$t('audit.endDate')"
              label-for="auditTo"
            >
              <flat-pickr
                v-model="filters.to"
                class="form-control"
                :placeholder="$t('audit.endDate')"
                :config="dateConfig"
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              :label="$t('audit.module')"
              label-for="auditModule"
            >
              <v-select
                v-model="filters.module"
                :options="$tOptions(moduleOptions)"
                :reduce="x => x.value"
                label="label"
                :placeholder="$t('audit.allModules')"
                :clearable="true"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              :label="$t('audit.actionType')"
              label-for="auditAction"
            >
              <v-select
                v-model="filters.action"
                :options="$tOptions(actionOptions)"
                :reduce="x => x.value"
                label="label"
                :placeholder="$t('audit.allActions')"
                :clearable="true"
                :taggable="true"
                :create-option="createActionOption"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              :label="$t('audit.entityType')"
              label-for="auditEntityType"
            >
              <v-select
                v-model="filters.entityType"
                :options="$tOptions(entityOptions)"
                :reduce="x => x.value"
                label="label"
                :placeholder="$t('audit.allEntityTypes')"
                :clearable="true"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              :label="$t('audit.outcome')"
              label-for="auditOutcome"
            >
              <v-select
                v-model="filters.outcome"
                :options="$tOptions(outcomeOptions)"
                :reduce="x => x.value"
                label="label"
                :placeholder="$t('audit.allOutcomes')"
                :clearable="true"
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              :label="$t('audit.actor')"
              label-for="auditActor"
            >
              <b-form-input
                v-model="filters.actor"
                :placeholder="$t('audit.actorPlaceholder')"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              :label="$t('audit.target')"
              label-for="auditTarget"
            >
              <b-form-input
                v-model="filters.target"
                :placeholder="$t('audit.targetPlaceholder')"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              :label="$t('audit.agent')"
              label-for="auditAgent"
            >
              <agent-selector
                v-model="selectedAgent"
                :auto-select="false"
                :placeholder="$t('audit.typeToSearch')"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              :label="$t('audit.entityId')"
              label-for="auditEntityId"
            >
              <b-form-input
                v-model="filters.entityId"
                :placeholder="$t('audit.entityIdPlaceholder')"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <div class="text-right">
          <b-button
            variant="outline-secondary"
            class="mr-1"
            @click="onReset"
          >
            {{ $t('audit.reset') }}
          </b-button>
          <b-button
            variant="primary"
            type="submit"
          >
            {{ $t('audit.search') }}
          </b-button>
        </div>
      </b-form>
    </b-card>

    <!-- Table Card -->
    <b-card no-body>
      <b-card-header class="d-flex justify-content-between align-items-center flex-wrap">
        <h4 class="mb-0">
          {{ $t('audit.listTitle') }}
          <small
            v-if="pagination.total"
            class="text-muted ml-1"
          >
            ({{ formatNumber(pagination.total) }} {{ $t('audit.items') }})
          </small>
        </h4>
      </b-card-header>
      <b-card-body>
        <b-table
          ref="auditTable"
          responsive
          :items="items"
          :fields="$tFields(columns)"
          :busy="loading"
          bordered
          show-empty
          :empty-text="$t('audit.noData')"
        >
          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner class="align-middle mr-1" />
              <strong>{{ $t('audit.loading') }}</strong>
            </div>
          </template>

          <template #cell(createAt)="row">
            {{ formatDateTime(row.item.createAt) }}
          </template>

          <template #cell(event)="row">
            <div>
              <b-badge variant="light-primary">
                {{ getModuleLabel(row.item.module) }}
              </b-badge>
            </div>
            <div class="font-weight-bold mt-25">
              {{ getActionLabel(row.item.action) }}
            </div>
          </template>

          <template #cell(outcome)="row">
            <b-badge :variant="getOutcomeVariant(row.item)">
              {{ getOutcomeLabel(row.item) }}
            </b-badge>
          </template>

          <template #cell(reason)="row">
            <div class="audit-reason">
              {{ getReason(row.item) }}
            </div>
          </template>

          <template #cell(actorAgent)="row">
            <div>{{ row.item.actor || '-' }}</div>
            <small class="text-secondary">
              {{ row.item.agent || '' }}
            </small>
          </template>

          <template #cell(targetEntity)="row">
            <div>{{ row.item.target || '-' }}</div>
            <small class="text-secondary">
              {{ getEntityLabel(row.item.entityType) }}
            </small>
            <small
              v-if="row.item.entityId"
              class="text-secondary"
            >
              {{ row.item.entityId }}
            </small>
          </template>

          <template #cell(ip)="row">
            <small
              class="text-secondary cursor-pointer"
              @click="copyToClipboard(row.item.ip)"
            >
              {{ row.item.ip || '-' }}
            </small>
          </template>

          <template #cell(actions)="row">
            <b-button
              v-b-tooltip.hover
              variant="flat-primary"
              size="sm"
              :title="$t('audit.viewDetail')"
              @click="openDetail(row.item)"
            >
              <feather-icon icon="EyeIcon" />
            </b-button>
          </template>
        </b-table>

        <b-row>
          <b-col cols="2">
            <b-form-group class="text-nowrap mb-0">
              <label class="d-inline-block mr-50 text-sm-right font-small-4">{{ $t('Items') }}</label>
              <b-form-select
                v-model="pagination.pageSize"
                size="sm"
                :options="pageSizeOptions"
                :disabled="loading"
                class="w-50"
                style="min-width: 100px"
                @change="onPageSizeChange"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            lg="8"
            md="6"
            sm="12"
          >
            <b-pagination
              v-model="pagination.page"
              :total-rows="pagination.total"
              :per-page="pagination.pageSize"
              :disabled="loading"
              align="center"
              size="sm"
              class="my-0"
              @change="onPageChange"
            />
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Detail Modal -->
    <b-modal
      v-model="showDetail"
      hide-footer
      centered
      size="lg"
      dialog-class="audit-detail-modal"
      :title="$t('audit.detailTitle')"
    >
      <div v-if="selected">
        <b-row class="mb-2">
          <b-col
            cols="12"
            md="6"
          >
            <strong>{{ $t('audit.detailModule') }}:</strong> {{ getModuleLabel(selected.module) }}<br>
            <strong>{{ $t('audit.detailAction') }}:</strong> {{ getActionLabel(selected.action) }}<br>
            <strong>{{ $t('audit.detailOutcome') }}:</strong>
            <b-badge :variant="getOutcomeVariant(selected)">
              {{ getOutcomeLabel(selected) }}
            </b-badge><br>
            <strong>{{ $t('audit.detailCategory') }}:</strong> {{ getCategoryLabel(selected.category) }}<br>
            <strong>{{ $t('audit.detailReason') }}:</strong> {{ getReason(selected) }}<br>
            <strong>{{ $t('audit.detailPolicy') }}:</strong> {{ getPolicy(selected) || '-' }}<br>
            <strong>{{ $t('audit.detailEntityType') }}:</strong>
            {{ getEntityLabel(selected.entityType) }}
            <span v-if="selected.entityId">({{ selected.entityId }})</span><br>
            <strong>{{ $t('audit.detailTime') }}:</strong> {{ formatDateTime(selected.createAt) }}
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <strong>{{ $t('audit.detailActor') }}:</strong> {{ selected.actor || '-' }}<br>
            <strong>{{ $t('audit.detailTarget') }}:</strong> {{ selected.target || '-' }}<br>
            <strong>{{ $t('audit.detailAgent') }}:</strong> {{ selected.agent || '-' }}<br>
            <strong>{{ $t('audit.detailRequest') }}:</strong>
            {{ selected.method || '-' }} {{ selected.path || '-' }}<br>
            <strong>{{ $t('audit.detailStatusCode') }}:</strong> {{ selected.statusCode || '-' }}<br>
            <strong>IP:</strong> {{ selected.ip || '-' }}<br>
            <strong>UA:</strong>
            <small class="text-secondary">{{ selected.userAgent || '-' }}</small>
          </b-col>
        </b-row>

        <hr>

        <b-row>
          <b-col
            cols="12"
            md="6"
          >
            <h5>{{ $t('audit.before') }}</h5>
            <div class="audit-json audit-json-before">
              <div
                v-for="(line, index) in beforeDiffLines"
                :key="`before-${index}`"
                :class="['audit-json-line', { 'audit-json-line-changed': line.changed }]"
              >
                <span
                  v-for="(part, partIndex) in line.parts"
                  :key="`before-${index}-${partIndex}`"
                  :class="{ 'audit-json-segment-changed': part.changed }"
                >{{ part.text }}</span>
              </div>
            </div>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <h5>{{ $t('audit.after') }}</h5>
            <div class="audit-json audit-json-after">
              <div
                v-for="(line, index) in afterDiffLines"
                :key="`after-${index}`"
                :class="['audit-json-line', { 'audit-json-line-changed': line.changed }]"
              >
                <span
                  v-for="(part, partIndex) in line.parts"
                  :key="`after-${index}-${partIndex}`"
                  :class="{ 'audit-json-segment-changed': part.changed }"
                >{{ part.text }}</span>
              </div>
            </div>
          </b-col>
        </b-row>

        <div v-if="selected.metadata">
          <h5>{{ $t('audit.metadata') }}</h5>
          <pre class="audit-json">{{ formatJson(selected.metadata) }}</pre>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody, BRow, BCol, BForm, BFormGroup, BFormInput,
  BFormSelect, BButton, BTable, BSpinner, BPagination, BBadge, BModal, VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import AgentSelector from '@core/components/agent/AgentSelector.vue'
import auditService from '@/service/auditService'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BButton,
    BTable,
    BSpinner,
    BPagination,
    BBadge,
    BModal,
    vSelect,
    flatPickr,
    AgentSelector,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    const now = new Date()
    const pad = n => String(n).padStart(2, '0')
    const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`

    return {
      loading: false,
      items: [],
      requestId: 0,
      pagination: {
        page: 1,
        pageSize: 50,
        total: 0,
      },
      pageSizeOptions: [25, 50, 100, 200],
      filters: {
        from: `${today} 00:00`,
        to: `${today} 23:59`,
        module: null,
        action: null,
        outcome: null,
        actor: '',
        target: '',
        entityType: null,
        entityId: '',
      },
      selectedAgent: null,
      dateConfig: {
        enableTime: true,
        time_24hr: true,
        dateFormat: 'Y-m-d H:i',
      },
      showDetail: false,
      selected: null,
    }
  },
  computed: {
    moduleMap() {
      return {
        Rate: this.$t('audit.moduleRate'),
        RateUser: this.$t('audit.moduleRateUser'),
        Limit: this.$t('audit.moduleLimit'),
        LimitUser: this.$t('audit.moduleLimitUser'),
        LimitNumber: this.$t('audit.moduleLimitNumber'),
        LimitPrice: this.$t('audit.moduleLimitPrice'),
        Agent: this.$t('audit.moduleAgent'),
        Member: this.$t('audit.moduleMember'),
        SubAccount: this.$t('audit.moduleSubAccount'),
        Huay: this.$t('audit.moduleHuay'),
        Yeekee: this.$t('audit.moduleYeekee'),
        Bet: this.$t('audit.moduleBet'),
        Customer: this.$t('audit.moduleCustomer'),
        SystemSetting: this.$t('audit.moduleSystemSetting'),
        Vip: this.$t('audit.moduleVip'),
        Authorization: this.$t('audit.moduleAuthorization'),
        Other: this.$t('audit.moduleOther'),
      }
    },
    entityMap() {
      return {
        HuayRate: this.$t('audit.entityHuayRate'),
        HuayRatePerUser: this.$t('audit.entityHuayRatePerUser'),
        YeekeeRate: this.$t('audit.entityYeekeeRate'),
        YeekeeRatePerUser: this.$t('audit.entityYeekeeRatePerUser'),
        HuayLimit: this.$t('audit.entityHuayLimit'),
        HuayLimitPerUser: this.$t('audit.entityHuayLimitPerUser'),
        HuayLimitRaw: this.$t('audit.entityHuayLimitRaw'),
        HuayLimitNumber: this.$t('audit.entityHuayLimitNumber'),
        Agent: this.$t('audit.entityAgent'),
        Member: this.$t('audit.entityMember'),
        Customer: this.$t('audit.entityCustomer'),
        Huay: this.$t('audit.entityHuay'),
        Yeekee: this.$t('audit.entityYeekee'),
        VipSetting: this.$t('audit.entityVipSetting'),
        SystemSetting: this.$t('audit.entitySystemSetting'),
        Other: this.$t('audit.entityOther'),
      }
    },
    actionMap() {
      return {
        UpdateCondition: this.$t('audit.actionUpdateCondition'),
        UpdateReward: this.$t('audit.actionUpdateReward'),
        UpdateConditionYeekee: this.$t('audit.actionUpdateConditionYeekee'),
        UpdateRewardYeekee: this.$t('audit.actionUpdateRewardYeekee'),
        Init: this.$t('audit.actionInit'),
        InitYeekee: this.$t('audit.actionInitYeekee'),
        Delete: this.$t('audit.actionDelete'),
        DeleteYeekee: this.$t('audit.actionDeleteYeekee'),
      }
    },
    outcomeMap() {
      return {
        Success: this.$t('audit.outcomeSuccess'),
        Denied: this.$t('audit.outcomeDenied'),
        Failed: this.$t('audit.outcomeFailed'),
        Locked: this.$t('audit.outcomeLocked'),
      }
    },
    categoryMap() {
      return {
        Business: this.$t('audit.categoryBusiness'),
        Authorization: this.$t('audit.categoryAuthorization'),
        LoginSecurity: this.$t('audit.categoryLoginSecurity'),
        Other: this.$t('audit.categoryOther'),
      }
    },
    moduleOptions() {
      return Object.entries(this.moduleMap).map(([value, label]) => ({ label, value }))
    },
    entityOptions() {
      return Object.entries(this.entityMap).map(([value, label]) => ({ label, value }))
    },
    actionOptions() {
      return Object.entries(this.actionMap).map(([value, label]) => ({ label, value }))
    },
    outcomeOptions() {
      return Object.entries(this.outcomeMap).map(([value, label]) => ({ label, value }))
    },
    columns() {
      return [
        { key: 'createAt', label: this.$t('audit.colTime'), sortable: false },
        { key: 'outcome', label: this.$t('audit.colOutcome'), sortable: false },
        { key: 'event', label: this.$t('audit.colEvent'), sortable: false },
        { key: 'actorAgent', label: this.$t('audit.colActorAgent'), sortable: false },
        { key: 'targetEntity', label: this.$t('audit.colTargetEntity'), sortable: false },
        { key: 'reason', label: this.$t('audit.colReason'), sortable: false },
        { key: 'ip', label: 'IP', sortable: false },
        { key: 'actions', label: '', sortable: false },
      ]
    },
    beforeDiffLines() {
      return this.getJsonDiffLines('before')
    },
    afterDiffLines() {
      return this.getJsonDiffLines('after')
    },
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      // กัน race: ถ้า user คลิก paginate รัวๆ จะมีหลาย request วิ่งพร้อมกัน
      // → เก็บ requestId ปัจจุบัน เเล้วเช็คก่อน apply response (ยึด response ล่าสุดเสมอ)
      this.requestId += 1
      const currentRequestId = this.requestId
      try {
        const params = new URLSearchParams()
        params.append('page', this.pagination.page)
        params.append('pageSize', this.pagination.pageSize)

        if (this.filters.module) params.append('module', this.filters.module)
        if (this.filters.action) params.append('action', this.filters.action)
        if (this.filters.outcome) params.append('outcome', this.filters.outcome)
        if (this.filters.actor) params.append('actor', this.filters.actor)
        if (this.filters.target) params.append('target', this.filters.target)
        if (this.filters.entityType) params.append('entityType', this.filters.entityType)
        if (this.filters.entityId) params.append('entityId', this.filters.entityId)
        if (this.selectedAgent && this.selectedAgent.code) {
          params.append('agent', this.selectedAgent.code)
        }
        if (this.filters.from) params.append('from', this.filters.from)
        if (this.filters.to) params.append('to', this.filters.to)

        const res = await auditService.getAuthen(`audit/logs?${params.toString()}`)
        if (currentRequestId !== this.requestId) return
        if (res.status === 200 && res.data) {
          this.items = res.data.items || []
          this.pagination.total = res.data.total || 0
        } else {
          this.items = []
          this.pagination.total = 0
        }
      } catch (e) {
        if (currentRequestId !== this.requestId) return
        this.items = []
        this.pagination.total = 0
      } finally {
        if (currentRequestId === this.requestId) this.loading = false
      }
    },
    onSearch() {
      this.pagination.page = 1
      this.loadData()
    },
    onPageChange(page) {
      this.pagination.page = page
      this.loadData()
    },
    onPageSizeChange() {
      this.pagination.page = 1
      this.loadData()
    },
    onReset() {
      const now = new Date()
      const pad = n => String(n).padStart(2, '0')
      const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
      this.filters = {
        from: `${today} 00:00`,
        to: `${today} 23:59`,
        module: null,
        action: null,
        outcome: null,
        actor: '',
        target: '',
        entityType: null,
        entityId: '',
      }
      this.selectedAgent = null
      this.onSearch()
    },
    getModuleLabel(v) {
      if (!v) return '-'
      return this.moduleMap[v] || v
    },
    getEntityLabel(v) {
      if (!v) return '-'
      return this.entityMap[v] || v
    },
    getActionLabel(v) {
      if (!v) return '-'
      const action = this.normalizeAction(v)
      return this.actionMap[action] || action
    },
    normalizeAction(v) {
      if (!v) return '-'
      return v.endsWith('Denied') ? v.replace(/Denied$/, '') : v
    },
    getOutcomeValue(v) {
      if (!v) return 'Success'
      if (typeof v === 'string') return v
      return v.outcome || (v.action && v.action.endsWith('Denied') ? 'Denied' : 'Success')
    },
    getOutcomeLabel(v) {
      const outcome = this.getOutcomeValue(v)
      return this.outcomeMap[outcome] || outcome
    },
    getCategoryLabel(v) {
      const category = v || 'Business'
      return this.categoryMap[category] || category
    },
    getReason(item) {
      if (!item) return '-'
      return item.reason || (item.metadata && item.metadata.reason) || '-'
    },
    getPolicy(item) {
      if (!item) return ''
      return item.policy || (item.metadata && item.metadata.policy) || ''
    },
    getOutcomeVariant(v) {
      switch (this.getOutcomeValue(v)) {
        case 'Denied':
          return 'danger'
        case 'Failed':
          return 'warning'
        case 'Locked':
          return 'dark'
        case 'Success':
          return 'success'
        default:
          return 'secondary'
      }
    },
    getStatusVariant(statusCode) {
      const code = Number(statusCode)
      if (code === 401 || code === 403) return 'danger'
      if (code >= 500) return 'warning'
      if (code >= 400) return 'secondary'
      return 'success'
    },
    createActionOption(v) {
      return { label: v, value: v }
    },
    openDetail(item) {
      this.selected = item
      this.showDetail = true
    },
    formatDateTime(v) {
      if (!v) return '-'
      const d = new Date(v)
      if (Number.isNaN(d.getTime())) return v
      const pad = n => String(n).padStart(2, '0')
      const yyyy = d.getFullYear()
      const MM = pad(d.getMonth() + 1)
      const dd = pad(d.getDate())
      const hh = pad(d.getHours())
      const mi = pad(d.getMinutes())
      const ss = pad(d.getSeconds())
      return `${yyyy}-${MM}-${dd} ${hh}:${mi}:${ss}`
    },
    formatNumber(n) {
      return Number(n || 0).toLocaleString()
    },
    formatJson(v) {
      if (v === null || v === undefined) return '-'
      try {
        return JSON.stringify(this.sortJsonSingle(v), null, 2)
      } catch (e) {
        return String(v)
      }
    },
    getJsonDiffLines(side) {
      if (!this.selected) return [{ changed: false, parts: [{ text: '-', changed: false }] }]

      const [beforeValue, afterValue] = this.sortJsonPair(this.selected.before, this.selected.after)
      const beforeLines = this.stringifyJson(beforeValue).split('\n')
      const afterLines = this.stringifyJson(afterValue).split('\n')
      const max = Math.max(beforeLines.length, afterLines.length)
      const lines = []

      for (let i = 0; i < max; i += 1) {
        const current = side === 'before' ? beforeLines[i] : afterLines[i]
        const other = side === 'before' ? afterLines[i] : beforeLines[i]
        const currentText = current === undefined ? '' : current
        const otherText = other === undefined ? '' : other
        const changed = currentText !== otherText

        lines.push({
          changed,
          parts: changed
            ? this.getJsonLineDiffParts(currentText, otherText)
            : [{ text: currentText || ' ', changed: false }],
        })
      }

      return lines
    },
    getJsonLineDiffParts(text, otherText) {
      const current = text || ' '
      const other = otherText || ' '
      let prefixLength = 0
      const maxPrefix = Math.min(current.length, other.length)

      while (prefixLength < maxPrefix && current[prefixLength] === other[prefixLength]) {
        prefixLength += 1
      }

      let currentEnd = current.length - 1
      let otherEnd = other.length - 1
      while (
        currentEnd >= prefixLength
        && otherEnd >= prefixLength
        && current[currentEnd] === other[otherEnd]
      ) {
        currentEnd -= 1
        otherEnd -= 1
      }

      const parts = []
      if (prefixLength > 0) {
        parts.push({ text: current.slice(0, prefixLength), changed: false })
      }

      parts.push({
        text: current.slice(prefixLength, currentEnd + 1) || ' ',
        changed: true,
      })

      if (currentEnd + 1 < current.length) {
        parts.push({ text: current.slice(currentEnd + 1), changed: false })
      }

      return parts
    },
    stringifyJson(v) {
      if (v === null || v === undefined) return '-'
      try {
        return JSON.stringify(v, null, 2)
      } catch (e) {
        return String(v)
      }
    },
    sortJsonPair(left, right) {
      if (Array.isArray(left) && Array.isArray(right)) {
        const [orderedLeft, orderedRight] = this.alignJsonArrays(left, right)
        const max = Math.max(orderedLeft.length, orderedRight.length)
        const leftResult = []
        const rightResult = []

        for (let i = 0; i < max; i += 1) {
          const hasLeft = i < orderedLeft.length
          const hasRight = i < orderedRight.length
          if (hasLeft && hasRight) {
            const [sortedLeft, sortedRight] = this.sortJsonPair(orderedLeft[i], orderedRight[i])
            leftResult.push(sortedLeft)
            rightResult.push(sortedRight)
          } else if (hasLeft) {
            leftResult.push(this.sortJsonSingle(orderedLeft[i]))
          } else if (hasRight) {
            rightResult.push(this.sortJsonSingle(orderedRight[i]))
          }
        }

        return [leftResult, rightResult]
      }

      if (this.isPlainObject(left) && this.isPlainObject(right)) {
        const leftResult = {}
        const rightResult = {}
        const keys = Array.from(new Set([...Object.keys(left), ...Object.keys(right)])).sort()

        keys.forEach(key => {
          const hasLeft = Object.prototype.hasOwnProperty.call(left, key)
          const hasRight = Object.prototype.hasOwnProperty.call(right, key)

          if (hasLeft && hasRight) {
            const [sortedLeft, sortedRight] = this.sortJsonPair(left[key], right[key])
            leftResult[key] = sortedLeft
            rightResult[key] = sortedRight
          } else if (hasLeft) {
            leftResult[key] = this.sortJsonSingle(left[key])
          } else if (hasRight) {
            rightResult[key] = this.sortJsonSingle(right[key])
          }
        })

        return [leftResult, rightResult]
      }

      return [this.sortJsonSingle(left), this.sortJsonSingle(right)]
    },
    sortJsonSingle(value) {
      if (Array.isArray(value)) {
        return this.sortJsonArray(value).map(v => this.sortJsonSingle(v))
      }

      if (this.isPlainObject(value)) {
        return Object.keys(value).sort().reduce((acc, key) => {
          acc[key] = this.sortJsonSingle(value[key])
          return acc
        }, {})
      }

      return value
    },
    alignJsonArrays(left, right) {
      const fields = this.getJsonArraySortFields(left, right)
      if (!fields) return [left, right]

      return [
        this.sortJsonArray(left, fields),
        this.sortJsonArray(right, fields),
      ]
    },
    sortJsonArray(value, sortFields) {
      const fields = sortFields || this.getJsonArraySortFields(value, value)
      if (!fields) return value

      return value.slice().sort((a, b) => this.compareJsonArrayItems(a, b, fields))
    },
    getJsonArraySortFields(left, right) {
      const candidates = [
        ['HuayId', 'Username', 'Type'],
        ['HuayId', 'Agent', 'Type'],
        ['HuayId', 'Type'],
        ['Username', 'Type'],
        ['Agent', 'Type'],
        ['RoundId', 'Type'],
        ['Type'],
        ['Id'],
        ['id'],
        ['_id.$oid'],
        ['Code'],
        ['Name'],
        ['username', 'type'],
        ['agent', 'type'],
        ['huayId', 'type'],
        ['type'],
      ]

      return candidates.find(fields => (
        this.canSortJsonArrayByFields(left, fields)
        && this.canSortJsonArrayByFields(right, fields)
      )) || null
    },
    canSortJsonArrayByFields(value, fields) {
      if (!Array.isArray(value) || value.length === 0) return false
      const seen = new Set()

      for (let i = 0; i < value.length; i += 1) {
        if (!this.isPlainObject(value[i])) return false

        const key = this.getJsonArrayItemKey(value[i], fields)
        if (!key || seen.has(key)) return false
        seen.add(key)
      }

      return true
    },
    getJsonArrayItemKey(item, fields) {
      const values = []

      for (let i = 0; i < fields.length; i += 1) {
        const value = this.getJsonValueByPath(item, fields[i])
        if (value === null || value === undefined || value === '') return null
        values.push(String(value))
      }

      return values.join('\u0001')
    },
    getJsonValueByPath(item, path) {
      return path.split('.').reduce((current, key) => {
        if (current === null || current === undefined) return undefined
        return current[key]
      }, item)
    },
    compareJsonArrayItems(a, b, fields) {
      for (let i = 0; i < fields.length; i += 1) {
        const result = this.compareJsonSortValues(
          this.getJsonValueByPath(a, fields[i]),
          this.getJsonValueByPath(b, fields[i]),
        )
        if (result !== 0) return result
      }

      return this.stringifyJson(this.sortJsonSingle(a))
        .localeCompare(this.stringifyJson(this.sortJsonSingle(b)))
    },
    compareJsonSortValues(a, b) {
      const aNumber = Number(a)
      const bNumber = Number(b)
      const aIsNumber = a !== '' && !Number.isNaN(aNumber)
      const bIsNumber = b !== '' && !Number.isNaN(bNumber)

      if (aIsNumber && bIsNumber) return aNumber - bNumber

      return String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    },
    isPlainObject(value) {
      return Object.prototype.toString.call(value) === '[object Object]'
    },
    copyToClipboard(text) {
      if (!text) return
      if (navigator && navigator.clipboard) {
        navigator.clipboard.writeText(text)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@core/scss/vue/libs/vue-select.scss';
@import '~@core/scss/vue/libs/vue-flatpicker.scss';

.audit-json {
  background: #f8f8f8;
  padding: 0.75rem;
  border-radius: 0.25rem;
  font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
  font-size: 0.8rem;
  line-height: 1.45;
  max-height: 28vh;
  overflow: auto;
  white-space: pre;
  word-break: break-word;
}

.audit-json-line {
  min-height: 1.15rem;
  padding: 0 0.25rem;
  border-radius: 0.2rem;
}

.audit-json-before .audit-json-line-changed {
  background: #fff1f1;
  color: #c92a2a;
}

.audit-json-after .audit-json-line-changed {
  background: #eefbf3;
  color: #1f7a3d;
}

.audit-json-segment-changed {
  font-weight: 700;
}

::v-deep .audit-detail-modal {
  max-width: 960px;
}

::v-deep .audit-detail-modal .modal-body {
  max-height: 76vh;
  overflow-y: auto;
}

.cursor-pointer {
  cursor: pointer;
}

.audit-reason {
  max-width: 220px;
  word-break: break-word;
}

.audit-path {
  display: block;
  max-width: 220px;
  word-break: break-all;
}
</style>
